/**
 * SPC site copy. Edit text here; components read from these objects.
 * Anything wrapped in [PLACEHOLDER ...] renders as a highlighted chip until replaced.
 * Naming rule: always "SPC", never the full legal name.
 */

export const brand = {
  name: "SPC",
  descriptor: "Specialty pharma, diagnostics and ingredients",
  email: process.env.REACT_APP_CONTACT_EMAIL,
  whatsappLabel: "Message SPC on WhatsApp",
};

// `id` is used for test ids; items with a # in `to` are anchor links on the home page.
export const nav = [
  { id: "sourcing", label: "Sourcing", to: "/sourcing" },
  { id: "api-list", label: "API List", to: "/active-pharmaceutical-ingredients" },
  { id: "licensing", label: "Licensing", to: "/licensing" },
  { id: "rd-cro-services", label: "R&D and CRO", to: "/rd-cro-services" },
  { id: "market-entry", label: "Market Entry", to: "/#market-entry" },
  { id: "case-studies", label: "Case Studies", to: "/case-studies" },
  { id: "news", label: "News", to: "/news" },
  { id: "about", label: "About", to: "/about" },
  { id: "contact", label: "Contact", to: "/contact" },
];

export const regions = [
  { id: "europe", name: "Europe", note: "Innovators, licensors and specialty partners" },
  { id: "latam", name: "LATAM", note: "Regional pharma licensing and launch" },
  { id: "mena", name: "MENA", note: "Registration and distribution partners" },
  { id: "cis", name: "Russia and CIS", note: "Licensing and market access" },
  { id: "india", name: "India", note: "Operating base, sourcing and R&D gateway" },
  { id: "sea", name: "South East Asia", note: "Distributors and regional pharma" },
  { id: "korea", name: "Korea", note: "Dermatology and aesthetics innovators" },
  { id: "anz", name: "ANZ", note: "Specialty and consumer health partners" },
];

export const home = {
  meta: {
    title: "SPC | The bridge between innovators and markets",
    description:
      "SPC sources, licenses, develops and commercialises products in dermatology and hair science, diagnostics and nutraceuticals, for partners across Europe, LATAM, MENA, Russia and CIS, ANZ, South East Asia, Korea and India.",
  },
  hero: {
    eyebrow: "Specialty pharma · Diagnostics · Ingredients",
    lines: ["The bridge", "between *innovators*", "and markets."],
    value:
      "SPC sources, licenses, develops and commercialises differentiated products in dermatology and hair science, diagnostics and nutraceuticals, connecting innovators with the partners who take them to patients.",
    primary: "Start a conversation",
    secondary: "How we work",
    caption: "Offices: India, Switzerland and South Korea. Partners: worldwide.",
  },
  marquee: [
    "Dermatology and hair science",
    "Diagnostics",
    "Nutraceuticals",
    "APIs and ingredients",
    "Europe",
    "LATAM",
    "MENA",
    "Russia and CIS",
    "ANZ",
    "South East Asia",
    "Korea",
    "India",
  ],
  intro: {
    eyebrow: "What SPC does",
    text:
      "Great science rarely fails for lack of merit. It stalls between the lab and the market. SPC works in that gap: finding the right ingredient, the right licensee, the right lab and the right route to patients.",
  },
  services: [
    {
      id: "sourcing",
      index: "01",
      title: "Sourcing",
      body: "APIs, excipients, actives and nutraceutical ingredients from qualified suppliers, with the documentation and regulatory support buyers need.",
      points: ["Supplier qualification", "COA, MSDS, DMF or CEP where available", "Samples and logistics"],
      to: "/sourcing",
      image: "/images/capsules.webp",
    },
    {
      id: "licensing",
      index: "02",
      title: "Licensing and Market Access",
      body: "In-licensing differentiated, patented or proprietary products and sublicensing them to leading regional pharma partners.",
      points: ["In-licensing and sublicensing", "Regulatory pathway support", "Commercial launch partners"],
      to: "/licensing",
      image: "/images/cargo.webp",
      extra: { label: "See licensing and JV case studies", to: "/case-studies?filter=licensing-jv" },
    },
    {
      id: "rnd",
      index: "03",
      title: "R&D and CRO Services",
      body: "Formulation, analytical, preclinical and pilot work coordinated through qualified CROs, labs and institutes in India.",
      points: ["Formulation and analytics", "Permeation, delivery and stability", "Pilot batches and dossiers"],
      to: "/rd-cro-services",
      image: "/images/gloves.webp",
    },
  ],
  focus: {
    eyebrow: "Focus areas",
    title: "Four fields. One standard of rigour.",
    areas: [
      { id: "derm", title: "Dermatology and hair science", body: "Topicals, actives and platforms for skin, scalp and hair.", image: "/images/dropper.webp" },
      { id: "dx", title: "Diagnostics", body: "Screening and testing solutions brought to new lab and digital-health networks.", image: "/images/diagnostics.webp" },
      { id: "nutra", title: "Nutraceuticals", body: "Evidence-led ingredients and finished formats for global buyers.", image: "/images/capsules-close.webp" },
      { id: "pharma", title: "Pharmaceutical finished products and APIs", body: "Finished dosage forms with all documentation, plus active pharmaceutical ingredients and intermediates.", image: "/images/finished-products.webp" },
    ],
  },
  map: {
    eyebrow: "Where we work",
    title: "India is our gateway. The world is our market.",
    body:
      "SPC has offices in India, Switzerland and South Korea, and works with innovators, licensees and buyers across eight regions.",
  },
  stats: [
    { value: "9+", label: "Territories covered" },
    { value: "4", label: "Partner types: innovators, pharma, labs, distributors" },
    { value: "15+", label: "Years of experience" },
    { value: "4", label: "Focus areas: dermatology and hair, diagnostics, nutraceuticals, finished products and APIs" },
  ],
  cta: {
    title: "Have a product, a molecule or a market in mind?",
    body: "Tell us where you are and where you want to go. A member of the SPC team will reply personally.",
    button: "Start a conversation",
  },
};

// In-house regulatory capability: shown on the home page and the sourcing page.
export const regulatory = {
  eyebrow: "API and ingredient sourcing",
  title: "Documentation international buyers expect.",
  text:
    "SPC has an in-house regulatory team that prepares and manages the documentation international buyers expect: DMFs (including KDMF), CEPs, CoPPs, GMP and WHO GMP certificates, site master files, stability data, impurity and nitrosamine assessments, and full supplier qualification dossiers. We also coordinate patent reviews before supply starts.",
  link: { label: "See API export case studies", to: "/case-studies?filter=api-exports" },
};

export const marketEntry = {
  eyebrow: "Market entry",
  title: "For Manufacturers and Suppliers: Enter New Markets",
  intro:
    "You make a strong product. SPC gets it registered, priced and sold in markets where you have no presence yet. We work with API manufacturers, finished dosage makers and innovators from India, Europe and Asia who want to grow in Korea, Europe, Latin America, Africa, the Middle East and India.",
  items: [
    { id: "selection", title: "Market selection", body: "We identify where your product has demand, who the buyers are and what it will take to win them." },
    { id: "filings", title: "Regulatory filings", body: "Our regulatory team prepares DMF, KDMF, CEP and CTD dossiers, closes gaps against each country's requirements and answers authority and buyer questions." },
    { id: "matching", title: "Partner matching", body: "We introduce you to vetted distributors, licensees and manufacturing partners we already work with." },
    { id: "structuring", title: "Licensing and JV structuring", body: "We structure licensing, supply and joint venture agreements, including exclusivity, minimum quantities and pricing." },
    { id: "channel", title: "Channel protection", body: "We keep your pricing and customer relationships orderly, so one market does not undercut another." },
    { id: "launch", title: "Launch and account management", body: "After launch, we run business reviews, forecasts and day-to-day coordination between you and your partners." },
  ],
  steps: ["Tell us your product and target markets", "We assess the opportunity and regulatory path", "We match partners and manage entry"],
  cta: { label: "Discuss your market entry", to: "/contact?topic=Market%20entry" },
};

export const caseStudiesPage = {
  meta: {
    title: "Case Studies | SPC",
    description:
      "How SPC moves products, APIs and partnerships across borders: licensing, joint ventures, regulatory filings and market entry in India, Europe, Korea, Latin America and Africa.",
  },
  hero: {
    eyebrow: "Case studies",
    lines: ["Case *Studies*"],
    body: "How SPC moves products, APIs and partnerships across borders: from licensing and joint ventures to regulatory filings and market entry.",
  },
  cta: { title: "Have a product, API or market in mind? Talk to SPC." },
};

export const newsPage = {
  meta: {
    title: "News | SPC",
    description: "Licensing deals, launches and partnerships from SPC and our partners.",
  },
  hero: {
    eyebrow: "News",
    lines: ["News"],
    body: "Licensing deals, launches and partnerships from SPC and our partners.",
  },
};

export const cdmo = {
  eyebrow: "Manufacturing in India",
  title: "Licensed in. Made in India. Supplied where it is needed.",
  body:
    "SPC has its own network of qualified CDMOs in India. Once an innovative product is licensed, we can manufacture it in India and supply it within India or from India to other markets, across pharmaceuticals, cosmetics and food supplements.",
  categories: [
    { id: "pharma", label: "Pharmaceuticals" },
    { id: "cosmetics", label: "Cosmetics" },
    { id: "supplements", label: "Food supplements" },
  ],
  routes: [
    { id: "domestic", title: "India to India", body: "Local manufacture for Indian partners, avoiding import freight and customs duty on finished goods." },
    { id: "export", title: "India to other markets", body: "Manufacture in India and export to licensees and distributors in other markets." },
  ],
};

export const apiList = {
  meta: {
    title: "API and intermediates list: USDMF, EDMF, KDMF available | SPC",
    description:
      "Browse 230+ active pharmaceutical ingredients SPC sources from qualified manufacturers, with USDMF, EDMF, KDMF and DMF availability. Request a quote on any product.",
  },
  hero: {
    eyebrow: "API and intermediates list",
    lines: ["Active ingredients,", "*documented*", "and ready to quote."],
    body: "Search the APIs SPC sources through its qualified manufacturer network. Each listing shows the regulatory documents available. Request a quote on any product and you get a first response within 24 to 48 hours.",
  },
  note:
    "Availability, documentation and pricing are confirmed per enquiry and per market. Products marked In development are not yet commercially available. Cannot find a product? SPC sources beyond this list, so send a sourcing enquiry.",
  teaser: {
    eyebrow: "API list",
    title: "Browse 230+ APIs with their DMF status.",
    body: "Search by product or therapeutic area, filter by USDMF, EDMF, KDMF or DMF, and request a quote in one click.",
    cta: "Open the API list",
  },
};

export const sourcing = {
  meta: {
    title: "API and ingredient sourcing for India and global buyers | SPC",
    description:
      "SPC sources APIs, excipients, cosmetic and dermatology actives and nutraceutical ingredients from qualified suppliers, with COA, MSDS, DMF or CEP documentation where available.",
  },
  hero: {
    eyebrow: "API and ingredient sourcing",
    lines: ["Ingredients", "you can *document*,", "not just buy."],
    body:
      "SPC sources APIs, excipients, actives and nutraceutical ingredients for Indian and international buyers. Every request is matched to qualified suppliers and backed by the documentation your quality and regulatory teams expect.",
    image: "/images/capsules-close.webp",
  },
  pillars: [
    { title: "Supplier qualification", body: "We assess manufacturers on capability, quality systems and track record before we put them in front of you, and audit our manufacturers periodically." },
    { title: "Documentation", body: "COA and MSDS as standard. DMF or CEP where available for the specific grade and source, plus technical data and specifications." },
    { title: "Regulatory support", body: "Help with import requirements, grade selection and the documents needed for your filing or customer audit." },
    { title: "Logistics", body: "Sampling, packing, export and delivery to your site, coordinated end to end with you kept informed." },
  ],
  categories: [
    { id: "apis", title: "APIs", body: "Active pharmaceutical ingredients across therapeutic areas, in USP, EP, IP, BP or in-house grades.", tag: "Pharma" },
    { id: "actives", title: "Cosmetic and dermatology actives", body: "Peptides, botanicals and functional actives for skin, scalp and hair formulations.", tag: "Derma" },
    { id: "nutra", title: "Nutraceutical ingredients", body: "Vitamins, botanical extracts, probiotics and specialty nutrients for supplements and functional foods.", tag: "Nutra" },
    { id: "rare", title: "Specialty and rare ingredients", body: "Hard-to-find molecules, low-volume intermediates and niche grades sourced on request.", tag: "Specialty" },
    { id: "fdf", title: "Tender supplies of FDF", body: "Finished dosage forms supplied for government, hospital and institutional tenders, from qualified manufacturers with the quality and registration documents each tender requires.", tag: "Tenders" },
  ],
  steps: [
    { title: "Enquiry", body: "Share the product, grade, quantity and destination. You get a first response within 24 to 48 hours." },
    { title: "Supplier match and samples", body: "We shortlist qualified suppliers and arrange samples with preliminary documentation." },
    { title: "Quality and documentation", body: "COA, MSDS and DMF or CEP where available are reviewed with your QA team before commitment." },
    { title: "Supply", body: "Commercial terms, packing, shipping and delivery to your target location, with ongoing supply support." },
  ],
  formIntro: {
    eyebrow: "Sourcing enquiry",
    title: "Tell us what you need.",
    body: "The more detail you share, the faster we can match a supplier. Specification sheets can be sent once we reply.",
  },
};

export const licensing = {
  meta: {
    title: "Licensing and market access | SPC",
    description:
      "SPC in-licenses differentiated, patented or proprietary products and sublicenses them to leading regional pharma partners, with regulatory and commercial support.",
  },
  hero: {
    eyebrow: "Licensing and market access",
    lines: ["Good products", "deserve *more*", "markets."],
    body:
      "SPC in-licenses differentiated, patented or proprietary products and sublicenses them to leading regional pharma partners. We stay involved after signature, with regulatory and commercial support until the product reaches patients.",
    image: "/images/cargo.webp",
  },
  model: [
    { title: "In-license", body: "We secure rights to differentiated, patented or proprietary products from innovators and biotech founders." },
    { title: "Sublicense", body: "We place those rights with leading pharma partners in each region, matched on capability and reach." },
    { title: "Support", body: "We coordinate regulatory dossiers, local requirements and launch planning so the deal turns into supply." },
  ],
  flow: [
    { id: "innovator", title: "Innovator", body: "IP, data and product", link: "Licence" },
    { id: "spc", title: "SPC", body: "Rights, regulatory and commercial bridge", link: "Sublicence" },
    { id: "partner", title: "Regional partner", body: "Registration, manufacture or import, sales", link: "Launch" },
    { id: "market", title: "Patient and market", body: "Access to differentiated care" },
  ],
  examplesIntro: {
    eyebrow: "Examples",
    title: "How the model works in practice.",
  },
  paths: [
    {
      id: "license-out",
      title: "I have a product to license",
      body: "You are an innovator, biotech founder or brand owner with a differentiated product and want partners in new territories.",
      button: "Discuss my product",
      topic: "Licensing: I have a product to license",
    },
    {
      id: "access",
      title: "I want to access a product for my market",
      body: "You are a pharma company or distributor looking for differentiated products to register and launch in your region.",
      button: "Find products for my market",
      topic: "Licensing: I want to access a product for my market",
    },
  ],
};

// /rd-cro-services. Confidential programmes: use only approved wording, no drug, molecule, indication or partner names.
const RD_CONTACT = `/contact?topic=${encodeURIComponent("R&D / CRO")}`;

export const rdCro = {
  meta: {
    title: "R&D and CRO Services in India | SPC",
    description:
      "SPC runs development and clinical programmes in India for international innovators, from formulation and stability to clinical studies and regulatory-ready data.",
  },
  hero: {
    eyebrow: "R&D and CRO",
    lines: ["R&D and CRO", "*Services*"],
    body: "SPC runs development and clinical programmes in India for international innovators, from formulation and stability to clinical studies and regulatory-ready data.",
    image: "/images/pipette.webp",
  },
  contactTo: RD_CONTACT,
  button: "Discuss your project",
  why: {
    id: "why",
    eyebrow: "Why India, why SPC",
    title: "Why India, why SPC",
    paragraphs: [
      "India offers world-class CDMOs, accredited CROs and experienced investigators at a fraction of Western costs. SPC is the single point of contact that makes this work for overseas companies: we select the right partners, structure the agreements, manage the project day to day, and deliver data packages that stand up to regulators in the markets you are targeting. Our in-house regulatory team makes sure every study is designed with the final dossier in mind.",
    ],
  },
  services: [
    { id: "clinical", title: "Clinical studies", body: "GCP-compliant investigator-initiated and sponsor studies with leading clinicians, including protocol, ethics approval, site management and reporting." },
    { id: "safety", title: "Safety testing", body: "Human patch tests and in vitro safety studies to OECD and Indian standards, including non-animal methods for markets that require them." },
    { id: "formulation", title: "Formulation development", body: "Development and technology transfer with top Indian CDMOs, covering long-acting injectables, oral films and topical products." },
    { id: "stability", title: "Stability studies", body: "ICH stability programmes, including Zone IVb conditions for hot and humid markets." },
    { id: "permeation", title: "In vitro permeation", body: "Skin permeation and release testing to support product claims and formulation choices." },
    { id: "regulatory", title: "Regulatory-ready data", body: "Study reports and dossiers prepared for submission in India, Latin America, the Middle East, Asia and the CIS." },
  ],
  steps: [
    "Scope the study and target markets",
    "Select and contract the right Indian partners",
    "Manage the programme and timelines",
    "Deliver the data package and support filings",
  ],
  cta: "Have a molecule, formulation or product that needs development or clinical data? Talk to SPC.",
  teaser: { eyebrow: "R&D and CRO", title: "R&D and CRO Services", button: "Learn more" },
  formIntro: {
    eyebrow: "Ask about an R&D project",
    title: "Tell us about your programme.",
    body: "Share where the project is today and where it needs to go. We will reply with an initial view on scope and approach.",
  },
};

export const about = {
  meta: {
    title: "About SPC",
    description: "SPC is a specialty pharma, diagnostics and ingredients partner operating from India and Switzerland and working with partners worldwide.",
  },
  hero: {
    eyebrow: "About SPC",
    lines: ["A scientific partner,", "not a *trading*", "portal."],
    body:
      "SPC exists to move good science across borders. We work with innovators who need markets, and with markets that need innovation.",
    image: "/images/microscope.webp",
  },
  story: [
    "From a base in India, with offices in Switzerland and South Korea, SPC sources, licenses, develops and commercialises products across dermatology and hair science, diagnostics and nutraceuticals. India gives us depth in manufacturing, research and talent. Our partners give us reach across Europe, LATAM, MENA, Russia and CIS, ANZ, South East Asia and Korea.",
  ],
  approach: [
    { title: "Science first", body: "We start with the data and the product, not the deal. If it is not differentiated, we say so." },
    { title: "Documented by default", body: "Specifications, quality documents and agreements are clear before anything moves." },
    { title: "Long-term partners", body: "We stay involved after signature, through registration, launch and resupply." },
    { title: "Discreet", body: "Confidential discussions stay confidential. Partner names are shared only with permission." },
  ],
  network: [
    { title: "Innovators and biotech founders", body: "Sources of differentiated, patented and proprietary products." },
    { title: "Regional pharma partners", body: "Licensees who register, launch and sell in their markets." },
    { title: "CROs, labs and institutes", body: "Qualified research partners in India for development work." },
    { title: "Manufacturers and suppliers", body: "Qualified sources of APIs, actives and ingredients." },
    { title: "Distributors", body: "Channel partners for ingredients and finished products." },
    { title: "Digital and lab networks", body: "Diagnostic and digital-health partners for patient reach." },
  ],
};

// Home page partner strip. `logo` files live in /public/partners; `mono` darkens white logos for the white tile; `tall` gives stacked logos more height.
export const partners = {
  eyebrow: "Partner network",
  title: "Working with innovators and leading pharma companies.",
  list: [
    { name: "Eris Lifesciences", logo: "/partners/eris-lifesciences.svg" },
    { name: "Dr. Reddy's", logo: "/partners/dr-reddys.png" },
    { name: "Cipla", logo: "/partners/cipla.svg" },
    { name: "Glenmark", logo: "/partners/glenmark.png", tall: true },
    { name: "Zydus", logo: "/partners/zydus.png" },
    { name: "Zambon", logo: "/partners/zambon.svg", mono: true },
    { name: "OFI Italy", logo: "/partners/ofi.webp", tall: true },
    { name: "SIFI", logo: "/partners/sifi.webp", tall: true },
    { name: "Fleming Laboratories", logo: "/partners/fleming-laboratories.png", tall: true },
    { name: "Modi-Mundipharma", logo: "/partners/modi-mundipharma.webp", tall: true },
    { name: "Caring Brands", logo: "/partners/caring-brands.png", mono: true },
    { name: "Jupiter Wellness", logo: "/partners/jupiter-wellness.svg" },
    { name: "New Day Diagnostics", logo: "/partners/new-day-diagnostics.png" },
    { name: "Applied Biology", logo: "/partners/applied-biology.webp" },
    { name: "BioQ", logo: "/partners/bioq.webp" },
    { name: "Follea", logo: "/partners/follea.webp", tall: true },
    { name: "Promediol", logo: "/partners/promediol.webp" },
    { name: "RIHU Healthcare", logo: "/partners/rihu-healthcare.webp" },
    { name: "Complex Science", logo: "/partners/complex-science.webp" },
  ],
};


// Map pins show city only. `address`, where set, appears on office cards and in the footer.
export const offices = [
  { id: "india", country: "India", city: "New Delhi", role: "Operating base", lat: 28.61, lon: 77.21 },
  { id: "switzerland", country: "Switzerland", city: "Lugano", role: "Partner office", address: "Via dei Piattini 5, 6926 Collina d'Oro, Switzerland", partner: { name: "Promediol", href: "https://promediol.com", logo: "/partners/promediol.webp" }, lat: 46.0, lon: 8.95 },
  { id: "korea", country: "South Korea", city: "Seoul", role: "Partner office", address: "5th Floor, Myungbo Building, 19 Jandari-ro 7-gil, Mapo-gu, Seoul 04034, South Korea", partner: { name: "RIHU Healthcare", href: "https://rihuhealthcare.imweb.me", logo: "/partners/rihu-healthcare.webp" }, lat: 37.57, lon: 126.98 },
];

export const contactPage = {
  meta: { title: "Contact SPC", description: "Start a conversation with SPC about sourcing, licensing, market access or R&D and CRO services." },
  hero: {
    eyebrow: "Contact",
    lines: ["Start a", "*conversation*."],
    body: "Tell us a little about you and what you are working on. We read every message and reply personally, by email or WhatsApp.",
  },
};

export const footer = {
  blurb: "The bridge between innovators and markets. Specialty pharma, diagnostics and ingredients.",
  disclaimer:
    "Information on this site is for business partners and professionals. Nothing here is medical advice or a promotional claim for any unapproved product.",
};
